import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ContactSection from "@/components/sections/ContactSection";
import useSeo from "@/hooks/useSeo";
import { api, resolveImage } from "@/lib/api";
import { CheckCircle2, Calendar, Gauge, Fuel } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("de-DE").format(n);

export default function VerkaufteFahrzeuge() {
  const [soldVehicles, setSoldVehicles] = useState(null);
  const [brand, setBrand] = useState("Alle");

  useEffect(() => window.scrollTo(0, 0), []);
  useSeo({
    title: "Verkaufte Fahrzeuge · Referenzen | DNA Automotive Köln",
    description: "Referenzen unserer bereits verkauften Premium- und Sportfahrzeuge in Köln. Vertrauen entsteht durch Erfahrung – DNA Automotive, MotorWorld Köln-Rheinland.",
    path: "/verkaufte-fahrzeuge",
    breadcrumbs: [{ name: "Verkaufte Fahrzeuge", url: "/verkaufte-fahrzeuge" }],
  });

  useEffect(() => {
    api
      .get("/vehicles", { params: { status: "sold", limit: 50 } })
      .then(({ data }) => setSoldVehicles(data))
      .catch(() => setSoldVehicles([]));
  }, []);

  const brands = useMemo(
    () => ["Alle", ...Array.from(new Set((soldVehicles || []).map((v) => v.brand)))],
    [soldVehicles]
  );

  const filtered = useMemo(
    () => (brand === "Alle" ? soldVehicles || [] : (soldVehicles || []).filter((v) => v.brand === brand)),
    [brand, soldVehicles]
  );

  return (
    <div className="bg-obsidian min-h-screen" data-testid="sold-page">
      <Header />

      {/* Hero */}
      <section className="relative pt-40 pb-32 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0 z-0 bg-obsidian-surface" />
        <div className="relative z-10 max-w-[1600px] mx-auto">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">Referenzen · Bereits verkauft</p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] max-w-4xl">
            Verkaufte<br /><span className="text-brand-bright">Fahrzeuge.</span>
          </h1>
          <p className="text-zinc-300 max-w-2xl text-lg mt-8">
            Eine Auswahl unserer erfolgreich verkauften Premium- und Sportfahrzeuge. Vertrauen entsteht durch Erfahrung.
          </p>
          <div className="flex flex-wrap gap-8 mt-10 text-zinc-300">
            <div>
              <div className="font-heading font-black text-brand-bright text-4xl md:text-5xl" data-testid="sold-count">
                {soldVehicles ? soldVehicles.length : "–"}
              </div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">Referenzen</div>
            </div>
            <div>
              <div className="font-heading font-black text-brand-bright text-4xl md:text-5xl">100%</div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">Diskrete Abwicklung</div>
            </div>
            <div>
              <div className="font-heading font-black text-brand-bright text-4xl md:text-5xl">
                {soldVehicles ? Math.max(brands.length - 1, 0) : "–"}
              </div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">Premium-Marken</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter */}
      {soldVehicles && soldVehicles.length > 0 && (
        <section className="px-6 md:px-10">
          <div className="max-w-[1600px] mx-auto flex flex-wrap gap-3 pb-10 border-b border-white/10">
            {brands.map((b) => (
              <button
                key={b}
                onClick={() => setBrand(b)}
                data-testid={`sold-filter-${b.toLowerCase()}`}
                className={`px-5 py-2.5 text-sm uppercase tracking-wider border transition-colors ${
                  brand === b ? "bg-brand text-black border-brand-light font-bold" : "text-zinc-300 border-white/15 hover:border-brand-light hover:text-brand-bright"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="px-6 md:px-10 py-16" data-testid="sold-grid">
        {soldVehicles === null ? null : filtered.length === 0 ? (
          <p className="max-w-[1600px] mx-auto text-zinc-400 text-lg py-20 text-center border border-white/10" data-testid="sold-empty">
            Aktuell werden hier unsere neuesten Verkäufe vorbereitet. Sprechen Sie uns gerne direkt an.
          </p>
        ) : (
          <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((v, i) => (
              <motion.article
                key={v.id || `${v.brand}-${v.model}-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.07, ease: [0.22, 1, 0.36, 1] }}
                className="group flex flex-col bg-obsidian-surface border border-white/10 hover:border-brand-light/40 transition-all duration-500"
                data-testid={`sold-card-${i}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={v.images?.[0] ? resolveImage(v.images[0]) : "/vehicle-placeholder.jpg"}
                    alt={`${v.brand} ${v.model} — verkauft`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-brand text-black text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">
                    <CheckCircle2 className="w-3 h-3" />
                    Verkauft
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-[11px] uppercase tracking-[0.25em] text-brand-bright mb-1">{v.brand}</span>
                  <h3 className="font-heading font-extrabold uppercase tracking-tight text-white text-xl md:text-2xl leading-tight">
                    {v.model}
                  </h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-[11px] text-zinc-400 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-brand-bright" />{v.year}</span>
                    <span className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-brand-bright" />{fmt(v.mileage)} km</span>
                    <span className="flex items-center gap-1.5"><Fuel className="w-3.5 h-3.5 text-brand-bright" />{v.fuel}</span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <p className="max-w-[1600px] mx-auto mt-16 text-center text-zinc-500 text-sm italic">
          Aus Diskretionsgründen zeigen wir keine detaillierten Käufer- oder Preisangaben.
        </p>
      </section>

      <ContactSection prefillVehicle="Referenz-Anfrage" />
      <Footer />
      <WhatsAppButton prefill="Hallo DNA Automotive, ich interessiere mich für Ihre verkauften Fahrzeuge." />
    </div>
  );
}
